import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select } from 'antd';
import { GroupNode, WithLatestStateDeviceNode } from '../RootTree';
import './styles.scss';
import { ROOT_GROUP_ID } from '../../../../constants/group';
import { useAdvanceSearchV2 } from './use-search';
import { buildTree } from '../../../../untils/advance-search';
import { debounce } from 'lodash';

export const AdvanceSearchComponent = ({
  namespace,
  onSelectCamera,
  onSelectGroup,
  getIsCameraPinned,
  onPinCamera,
}) => {
  const [applicationType, setApplicationType] = useState('device-group');
  const [isDebounce, setIsDebounce] = useState(false);
  const {
    search,
    data,
    isLoading: isSearchLoading,
    canLoadMore,
    loadMore,
  } = useAdvanceSearchV2({ namespace });
  const activeSearchValue = useRef('');

  const debounceSearch = useMemo(
    () =>
      debounce(
        (value) => {
          setIsDebounce(false);
          search({
            input: value,
            applicationType,
          });
        },
        2000,
        { leading: false, trailing: true }
      ),
    [applicationType, search]
  );

  const onChange = useCallback(
    (e) => {
      setIsDebounce(true);
      const value = e.target.value.trim();
      activeSearchValue.current = value;

      if (value) {
        debounceSearch(value);
      } else {
        setIsDebounce(false);
      }
    },
    [debounceSearch]
  );

  const isLoading = isDebounce || isSearchLoading;

  const onChangeApplicationType = useCallback(
    (value) => {
      setApplicationType(value);

      if (activeSearchValue.current) {
        search({
          input: activeSearchValue.current,
          applicationType: value,
        });
      }
    },
    [search]
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 10 }}>
        <Select
          value={applicationType}
          onChange={onChangeApplicationType}
          style={{ width: 150, flexShrink: 0 }}
        >
          <Select.Option value="device-group">Camera Group</Select.Option>
          <Select.Option value="device">Camera</Select.Option>
        </Select>
        <Input.Search
          autoFocus
          placeholder="Search"
          loading={isLoading}
          allowClear
          onChange={onChange}
        />
      </div>

      {data && (
        <SearchResult
          data={data}
          onSelectCamera={onSelectCamera}
          onSelectGroup={onSelectGroup}
          canLoadMore={canLoadMore}
          loadMore={loadMore}
          isLoading={isLoading}
          getIsCameraPinned={getIsCameraPinned}
          onPinCamera={onPinCamera}
        />
      )}
    </>
  );
};

const SearchResult = ({
  data,
  onSelectCamera,
  onSelectGroup,
  canLoadMore,
  loadMore,
  isLoading,
  getIsCameraPinned,
  onPinCamera,
}) => {
  const { records } = data;

  const { TbDevice = [] } = records;

  const { rootGroups, allGroups } = useMemo(
    () => buildTree(records, data.applicationType),
    [records, data.applicationType]
  );

  const uniqueTbDevice = useMemo(() => {
    // findIndex not so fast but it's ok for now
    return TbDevice.filter(
      (device, index, self) =>
        self.findIndex((t) => t.serial === device.serial) === index
    );
  }, [TbDevice]);

  const getChildren = useCallback(
    (id) => {
      return Promise.resolve(
        allGroups[id]?.subGroups.map((group) => group.group) || []
      );
    },
    [allGroups]
  );

  const getCameras = useCallback(
    (id) => {
      return Promise.resolve(allGroups[id]?.cameras || []);
    },
    [allGroups]
  );

  const getIsExpanded = useCallback((id) => {
    return true;
  }, []);

  const getIsSelected = useCallback((id) => {
    return false;
  }, []);

  const onExpand = useCallback((id) => {
    console.log(id);
  }, []);

  const getCameraTooltip = useCallback(({ name, id }) => {
    return 'View this camera';
  }, []);

  const getGroupTooltip = useCallback(({ name, id }) => {
    return 'Go to group camera';
  }, []);

  const selectCamera = useCallback(
    (cam) => {
      onSelectCamera(cam);
    },
    [onSelectCamera]
  );

  const getParentPath = useCallback(
    (group) => {
      const result = [group.id];

      while (group.parentDeviceGroupId !== ROOT_GROUP_ID) {
        group = allGroups[group.parentDeviceGroupId].group;
        result.push(group.id);
      }

      result.push(ROOT_GROUP_ID);

      return result.reverse();
    },
    [allGroups]
  );

  const selectGroup = useCallback(
    (groupId) => {
      const group = allGroups[groupId].group;
      const groupWithParentPath = {
        ...group,
        parentPath: getParentPath(group),
      };

      onSelectGroup(groupWithParentPath);
    },
    [onSelectGroup, allGroups, getParentPath]
  );

  return (
    <div className="live-advance-search-result">
      {rootGroups.map((rootGroup) => (
        <GroupNode
          key={rootGroup.group.id}
          name={rootGroup.group.name}
          id={rootGroup.group.id}
          getChildren={getChildren}
          getCameras={getCameras}
          getIsExpanded={getIsExpanded}
          getIsSelected={getIsSelected}
          onSelect={selectGroup}
          onExpand={onExpand}
          level={0}
          getTooltip={getGroupTooltip}
          getCameraTooltip={getCameraTooltip}
          onSelectCamera={selectCamera}
          getIsCameraPinned={getIsCameraPinned}
          onPinCamera={onPinCamera}
        />
      ))}

      <div className="camera-list">
        {uniqueTbDevice.map((device) => (
          <WithLatestStateDeviceNode
            key={device.serial}
            level={0}
            serial={device.serial}
            name={device.name}
            tooltip={getCameraTooltip(device)}
            onSelect={() => selectCamera(device)}
            isSelected={false}
            state={device.state}
            isPinned={getIsCameraPinned?.(device.serial) || false}
            onPin={(pinned) => onPinCamera(device, pinned)}
          />
        ))}
      </div>
      {canLoadMore && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button loading={isLoading} onClick={loadMore} disabled={isLoading}>
            More results
          </Button>
        </div>
      )}
    </div>
  );
};

export const AdvanceSearch = ({
  namespace,
  onSelectCamera,
  onSelectGroup,
  getIsCameraPinned,
  onPinCamera,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Input.Search
        placeholder="Search"
        onClick={() => setIsOpen(true)}
        readOnly
      />
      <Modal
        title="Advance Search"
        visible={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        destroyOnClose
      >
        <AdvanceSearchComponent
          namespace={namespace}
          onSelectCamera={(cam) => {
            onSelectCamera(cam);
            setIsOpen(false);
          }}
          onSelectGroup={(group) => {
            onSelectGroup(group);
            setIsOpen(false);
          }}
          getIsCameraPinned={getIsCameraPinned}
          onPinCamera={onPinCamera}
        />
      </Modal>
    </>
  );
};
